import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { zoomCard } from "@/components/CardZoom";
import { Loader2, X, Lock, KeyRound, Gift, ArchiveRestore, ArchiveX, Vault } from "lucide-react";

let _actx;
const _ctx = () => (_actx = _actx || new (window.AudioContext || window.webkitAudioContext)());
const sndKeyTurn = () => {
  try {
    const c = _ctx();
    const click = (at) => {
      const t = c.currentTime + at;
      const o = c.createOscillator(), g = c.createGain();
      o.type = "square"; o.frequency.setValueAtTime(2400, t); o.frequency.exponentialRampToValueAtTime(700, t + 0.05);
      g.gain.setValueAtTime(0.09, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.07);
      o.connect(g).connect(c.destination); o.start(t); o.stop(t + 0.09);
    };
    click(0); click(0.22); click(0.4);
  } catch { /* audio unsupported */ }
};
const sndWhoosh = () => {
  try {
    const c = _ctx(), t = c.currentTime, dur = 1.1;
    const buf = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const n = c.createBufferSource(); n.buffer = buf;
    const f = c.createBiquadFilter(); f.type = "lowpass";
    f.frequency.setValueAtTime(300, t); f.frequency.exponentialRampToValueAtTime(3800, t + dur * 0.8);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.28, t + dur * 0.55);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    n.connect(f).connect(g).connect(c.destination); n.start(t); n.stop(t + dur);
  } catch { /* audio unsupported */ }
};
const sndVaultThud = () => {
  try {
    const c = _ctx(), t = c.currentTime;
    const o = c.createOscillator(), g = c.createGain();
    o.type = "sine"; o.frequency.setValueAtTime(110, t); o.frequency.exponentialRampToValueAtTime(34, t + 0.6);
    g.gain.setValueAtTime(0.55, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
    o.connect(g).connect(c.destination); o.start(t); o.stop(t + 1.2);
    const buf = c.createBuffer(1, c.sampleRate * 0.14, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (d.length / 5));
    const n = c.createBufferSource(); n.buffer = buf;
    const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = 1600;
    const ng = c.createGain(); ng.gain.setValueAtTime(0.22, t);
    n.connect(f).connect(ng).connect(c.destination); n.start(t);
  } catch { /* audio unsupported */ }
};

const SDB_CSS = `
@keyframes sdbShake { 0%,100%{transform:scale(1) rotate(0)} 20%{transform:scale(1.02) rotate(-1.2deg)} 40%{transform:scale(1.02) rotate(1.2deg)} 60%{transform:scale(1.03) rotate(-0.8deg)} 80%{transform:scale(1.03) rotate(0.8deg)} }
@keyframes sdbSuck { 0%{transform:scale(1.04);filter:blur(0) brightness(1);opacity:1} 55%{transform:scale(2.4);filter:blur(2px) brightness(1.08);opacity:1} 100%{transform:scale(9);filter:blur(18px) brightness(.15);opacity:0} }
@keyframes sdbTunnel { 0%{opacity:0} 70%{opacity:0} 100%{opacity:1} }
@keyframes sdbZoom { 0%{transform:scale(1)} 25%{transform:scale(1.06)} 100%{transform:scale(2.6)} }
@keyframes sdbPulse { 0%,100%{box-shadow:0 0 0 0 rgba(253,224,71,.8)} 50%{box-shadow:0 0 0 16px rgba(253,224,71,0)} }
@keyframes sdbRise { from{transform:translateY(48px);opacity:0} to{transform:translateY(0);opacity:1} }
@keyframes sdbFade { from{opacity:0} to{opacity:1} }
.sdb-shake{animation:sdbShake .5s ease-in-out}
.sdb-suck{animation:sdbSuck 1.5s cubic-bezier(.55,0,.85,.35) .55s forwards}
.sdb-tunnel{animation:sdbTunnel 2s linear forwards}
.sdb-zoomcam{animation:sdbZoom 2.3s cubic-bezier(.45,.05,.55,.95) forwards;transform-origin:63% 38%}
.sdb-marker{animation:sdbPulse 1s ease-in-out infinite}
.sdb-rise{animation:sdbRise .6s cubic-bezier(.2,.7,.3,1) both}
.sdb-fade{animation:sdbFade .7s ease both}
`;

function PerkCert({ p, onRequest, busy }) {
  return (
    <div className="border border-yellow-500/60 bg-gradient-to-br from-yellow-950/40 via-black to-black p-3 relative overflow-hidden" data-testid={`sdb-perk-${p.id}`}>
      <div className="absolute inset-0 pointer-events-none opacity-[0.05] bg-[repeating-linear-gradient(45deg,#FDE047_0,#FDE047_1px,transparent_1px,transparent_9px)]" />
      <div className="flex items-start gap-2.5">
        {p.card_url ? <img src={p.card_url} alt="" className="w-10 h-14 object-contain bg-black border border-yellow-500/30 shrink-0" /> : <Gift className="w-8 h-8 text-yellow-400 shrink-0 mt-1" />}
        <div className="flex-1 min-w-0">
          <div className="text-[8px] font-black uppercase tracking-[0.3em] text-yellow-500/80 mb-0.5">Bullpen Perk Certificate</div>
          <div className="text-sm font-bold text-yellow-300 leading-tight">{p.perks}</div>
          {p.card_name && <div className="text-[10px] text-zinc-500 mt-0.5">won pulling {p.card_name}'s bounty card</div>}
        </div>
        <span className={`shrink-0 text-[8px] font-black uppercase tracking-widest px-2 py-1 border ${
          p.delivered ? "border-green-500/60 text-green-400" : p.redemption_requested ? "border-pb-cyan/60 text-pb-cyan" : "border-amber-500/60 text-amber-400"}`}
          data-testid={`sdb-perk-status-${p.id}`}>
          {p.delivered ? "Fulfilled" : p.redemption_requested ? "Requested" : "Owed"}
        </span>
      </div>
      {!p.delivered && !p.redemption_requested && (
        <button data-testid={`sdb-perk-redeem-${p.id}`} disabled={busy} onClick={() => onRequest(p)}
          className="mt-2.5 w-full bg-yellow-500 text-black text-[10px] font-black uppercase tracking-widest py-2 hover:bg-yellow-400 active:scale-[0.98] transition-all disabled:opacity-50">
          Request Redemption
        </button>
      )}
    </div>
  );
}

function CardTile({ c, label, onAction, busy, testid }) {
  return (
    <div className="border border-pb-border bg-pb-surface p-1.5" data-testid={testid}>
      <img src={c.url} alt={c.subject_name} loading="lazy" className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in"
        onClick={() => zoomCard(c.url, c.subject_name, null, c.game)} />
      <div className="text-[9px] font-semibold truncate mt-1">{c.subject_name}</div>
      <div className="text-[7.5px] text-zinc-500 truncate">{c.grade_label || c.rarity}</div>
      <button disabled={busy} onClick={onAction} data-testid={`${testid}-action`}
        className={`mt-1 w-full text-[8px] font-black uppercase tracking-widest py-1.5 border transition-all active:scale-95 disabled:opacity-50 ${
          label === "Deposit" ? "border-yellow-500/60 text-yellow-400 hover:bg-yellow-500/10" : "border-pb-cyan/60 text-pb-cyan hover:bg-pb-cyan/10"}`}>
        {label}
      </button>
    </div>
  );
}

function BoxInterior({ data, onClose, refresh }) {
  const [tray, setTray] = useState("cards");
  const [depositing, setDepositing] = useState(false);
  const [available, setAvailable] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadAvailable = useCallback(() => {
    api.get("/vault/mine").then(({ data }) => setAvailable(data.copies)).catch(() => setAvailable([]));
  }, []);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await refresh(); loadAvailable(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="absolute inset-0 overflow-y-auto" data-testid="sdb-interior">
      <img src="/sdb/drawer.jpg" alt="" className="fixed inset-0 w-full h-full object-cover brightness-[0.3] sdb-fade" />
      <div className="relative max-w-3xl mx-auto px-4 py-8 sdb-rise">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2.5">
            <KeyRound className="w-5 h-5 text-yellow-400" />
            <span className="font-display font-black uppercase text-xl text-yellow-400">Box #{data.box_number}</span>
          </div>
          <button data-testid="sdb-close" onClick={onClose}
            className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:border-white hover:text-white transition-colors">
            <X className="w-3.5 h-3.5" /> Lock &amp; Leave
          </button>
        </div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-5">
          Private vault of {data.owner_name} · contents hidden from everyone
        </p>
        <div className="flex gap-2 mb-4">
          <button data-testid="sdb-tray-cards" onClick={() => setTray("cards")}
            className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 border transition-colors ${tray === "cards" ? "bg-yellow-500 text-black border-yellow-500" : "border-pb-border text-zinc-400 hover:border-yellow-500/60"}`}>
            Stored Cards ({data.cards.length})
          </button>
          <button data-testid="sdb-tray-perks" onClick={() => setTray("perks")}
            className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 border transition-colors ${tray === "perks" ? "bg-yellow-500 text-black border-yellow-500" : "border-pb-border text-zinc-400 hover:border-yellow-500/60"}`}>
            My Perks ({data.perks.length})
          </button>
        </div>
        {tray === "cards" && (
          <div className="border border-yellow-500/30 bg-black/70 backdrop-blur-sm p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                Cards in here are invisible on your collection, off the market, and locked out of Card Wars.
              </span>
              <button data-testid="sdb-deposit-toggle" onClick={() => { setDepositing((d) => !d); if (!available) loadAvailable(); }}
                className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border transition-colors ${depositing ? "bg-pb-elevated text-white border-zinc-400" : "border-yellow-500/60 text-yellow-400 hover:bg-yellow-500/10"}`}>
                {depositing ? "Done Depositing" : "+ Deposit Cards"}
              </button>
            </div>
            {depositing && (
              <div className="mb-4 border-b border-pb-border pb-4">
                <div className="text-[9px] font-black uppercase tracking-[0.25em] text-yellow-500 mb-2">From your collection — tap Deposit to lock away</div>
                {!available ? <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" /> : available.length === 0 ? (
                  <div className="text-xs text-zinc-500">Nothing left in your open collection.</div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {available.map((c) => (
                      <CardTile key={c.id} c={c} label="Deposit" busy={busy} testid={`sdb-avail-${c.id}`}
                        onAction={() => act(() => api.post(`/depositbox/cards/${c.id}`), `${c.subject_name}'s card is locked in your box`)} />
                    ))}
                  </div>
                )}
              </div>
            )}
            {data.cards.length === 0 ? (
              <div className="text-sm text-zinc-500 border border-dashed border-pb-border p-8 text-center" data-testid="sdb-empty-cards">
                Your box is empty — deposit cards you want tucked away from your collection and the market.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="sdb-stored-grid">
                {data.cards.map((c) => (
                  <CardTile key={c.id} c={c} label="Take Out" busy={busy} testid={`sdb-stored-${c.id}`}
                    onAction={() => act(() => api.delete(`/depositbox/cards/${c.id}`), `${c.subject_name}'s card is back in your collection`)} />
                ))}
              </div>
            )}
          </div>
        )}
        {tray === "perks" && (
          <div className="border border-yellow-500/30 bg-black/70 backdrop-blur-sm p-4">
            {data.perks.length === 0 ? (
              <div className="text-sm text-zinc-500 border border-dashed border-pb-border p-8 text-center" data-testid="sdb-empty-perks">
                No perk certificates yet — pull a bounty card with a perk attached and it lands here automatically.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="sdb-perks-grid">
                {data.perks.map((p) => (
                  <PerkCert key={p.id} p={p} busy={busy}
                    onRequest={(perk) => act(() => api.post(`/depositbox/perks/${perk.id}/request`), "Redemption requested — the Bullpen has been notified")} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DepositBoxOverlay({ onClose }) {
  const [stage, setStage] = useState("door");
  const [data, setData] = useState(null);
  const refresh = useCallback(async () => {
    const { data } = await api.get("/depositbox");
    setData(data);
  }, []);
  useEffect(() => { refresh().catch(() => {}); }, [refresh]);
  useEffect(() => {
    if (stage === "door") {
      const w = setTimeout(sndWhoosh, 550);
      const t = setTimeout(() => setStage("vault"), 2100);
      return () => { clearTimeout(t); clearTimeout(w); };
    }
    if (stage === "vault") { const t = setTimeout(() => setStage("open"), 2500); return () => clearTimeout(t); }
    if (stage === "open") sndVaultThud();
  }, [stage]);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[90] bg-black overflow-hidden" data-testid="sdb-overlay">
      <style>{SDB_CSS}</style>
      {stage !== "open" && (
        <button data-testid="sdb-skip" onClick={() => setStage("open")}
          className="absolute top-4 right-4 z-20 text-[10px] font-black uppercase tracking-widest border border-zinc-700 text-zinc-500 px-3 py-1.5 hover:text-white hover:border-white transition-colors">
          Skip
        </button>
      )}
      {stage === "door" && (
        <div className="absolute inset-0 flex items-center justify-center" data-testid="sdb-stage-door">
          <div className="sdb-shake">
            <img src="/sdb/door.jpg" alt="Safety deposit box" className="sdb-suck w-[min(72vmin,560px)] shadow-[0_0_120px_rgba(253,224,71,.15)]" />
          </div>
          <div className="sdb-tunnel absolute inset-0 bg-black" />
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-[10px] font-black uppercase tracking-[0.4em] text-yellow-500/70 animate-pulse">
            Unlocking your box…
          </div>
        </div>
      )}
      {stage === "vault" && (
        <div className="absolute inset-0" data-testid="sdb-stage-vault">
          <div className="sdb-zoomcam absolute inset-0">
            <img src="/sdb/vault.jpg" alt="Bank vault" className="w-full h-full object-cover" />
            <div className="sdb-marker absolute left-[63%] top-[38%] -translate-x-1/2 -translate-y-1/2 w-14 h-10 border-2 border-yellow-400" />
          </div>
          <div className="absolute inset-x-0 bottom-10 text-center">
            <span className="text-[11px] font-black uppercase tracking-[0.4em] text-yellow-400 bg-black/70 px-4 py-2 border border-yellow-500/40">
              Locating box {data ? `#${data.box_number}` : "…"}
            </span>
          </div>
        </div>
      )}
      {stage === "open" && (
        data ? <BoxInterior data={data} onClose={onClose} refresh={refresh} />
          : <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-8 h-8 text-yellow-400 animate-spin" /></div>
      )}
    </div>
  );
}

export default function DepositBoxEntry() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="sdb-entry-btn" onClick={() => { sndKeyTurn(); setOpen(true); }} title="Open your private safety deposit box"
        className="group relative z-30 flex items-center gap-2 shrink-0 rounded-full border border-yellow-600/50 bg-gradient-to-r from-zinc-900 to-black pl-1 pr-3.5 py-1 hover:border-yellow-400 hover:shadow-[0_0_14px_rgba(253,224,71,.25)] transition-all active:scale-95">
        <span className="relative w-7 h-7 rounded-full overflow-hidden border border-yellow-600/60 shrink-0">
          <img src="/sdb/door.jpg" alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
        </span>
        <span className="text-[10px] font-black uppercase tracking-widest text-yellow-500 group-hover:text-yellow-300 transition-colors flex items-center gap-1.5">
          <Lock className="w-3 h-3" /> My Deposit Box
        </span>
      </button>
      {open && <DepositBoxOverlay onClose={() => setOpen(false)} />}
    </>
  );
}
